import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as Sentry from "@sentry/react";
import { User, Gym, Module, UserRole, AuthContextType, SaasTier } from '@/types';
import { supabase } from '@/lib/supabase';
import { clearSentryUser, reportError } from '@/lib/sentry';

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const GYM_SELECTION_KEY = 'gym_saas_selected_gym';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [gym, setGym] = useState<Gym | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionLock, setSessionLock] = useState(false);
  const userRef = React.useRef<User | null>(user);
  const sessionLockRef = React.useRef(sessionLock);

  useEffect(() => {
    userRef.current = user;
    sessionLockRef.current = sessionLock;
  }, [user, sessionLock]);

  // Helper to sync Supabase Session with App User State
  const syncSession = useCallback(async (sessionUser: any, force: boolean = false) => {
    if (sessionLockRef.current && !force) {
      console.log('[AuthContext] Session synchronization is LOCKED. Ignoring event.');
      return;
    }
    if (!sessionUser) {
      setUser(null);
      setGym(null);
      setModules([]);
      setIsLoading(false);
      return;
    }

    // Use ref to compare current user ID
    if (userRef.current && userRef.current.user_id !== sessionUser.id && !force) {
      console.log('[AuthContext] Ignoring session change for different user:', sessionUser.id);
      return;
    }

    try {
      // 1. Fetch Profile to get Role and Gym ID
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', sessionUser.id)
        .single();

      if (profileError || !profile) {
        console.warn('[AuthContext] Profile not found for user:', sessionUser.id, '- ignoring');
        setIsLoading(false);
        return;
      }

      // Check for account deactivation
      if (profile.active === false) {
        console.warn('[AuthContext] Account is deactivated for user:', sessionUser.id);
        await supabase.auth.signOut();
        setUser(null);
        setGym(null);
        setModules([]);
        setIsLoading(false);
        return;
      }

      // 2. Fetch Gym Details (for Modules) unless Super Admin with no Gym
      let currentGym = null;
      let enabledModules: string[] = [];

      if (profile.gym_id) {
        const { data: gymData, error: gymError } = await supabase
          .from('gyms')
          .select('*, tier:saas_tiers(*)')
          .eq('id', profile.gym_id)
          .single();

        if (gymData) {
          const saasTier = gymData.tier as SaasTier | null;

          currentGym = {
            ...gymData,
            gym_id: gymData.id,
            sheet_id: 'supabase_managed',
            plan: saasTier?.name || gymData.plan,
            tier: saasTier,
            price: saasTier?.price || gymData.price,
            modules_enabled: gymData.modules_enabled || [],
          } as Gym;

          // Combine modules from tier and manual overrides
          const tierModules = saasTier?.included_modules || [];
          const manualModules = gymData.modules_enabled || [];
          enabledModules = Array.from(new Set([...tierModules, ...manualModules]));
        }
      }

      // 3. Construct User Object
      const appUser: User = {
        user_id: sessionUser.id,
        email: sessionUser.email!,
        name: profile.full_name || sessionUser.email!,
        role: profile.role as UserRole,
        gym_id: profile.gym_id,
        active: profile.active ?? true,
        created_at: profile.created_at,
        alerts_disabled: profile.alerts_disabled || false,
        terms_accepted: profile.terms_accepted || false,
        terms_accepted_at: profile.terms_accepted_at,
        terms_accepted_ip: profile.terms_accepted_ip,
      };

      // 4. Persistence: If super_admin and no gym assigned, try to restore from localStorage
      if (appUser.role === 'super_admin' && !currentGym) {
        const persistedGymId = localStorage.getItem(GYM_SELECTION_KEY);
        if (persistedGymId) {
          try {
            const { data: gymData } = await supabase
              .from('gyms')
              .select('*, tier:saas_tiers(*)')
              .eq('id', persistedGymId)
              .single();

            if (gymData) {
              const saasTier = gymData.tier as SaasTier | null;
              currentGym = {
                ...gymData,
                gym_id: gymData.id,
                sheet_id: 'supabase_managed',
                plan: saasTier?.name || gymData.plan,
                tier: saasTier,
                price: saasTier?.price || gymData.price,
                modules_enabled: gymData.modules_enabled || [],
              } as Gym;

              // Update modules for the restored gym
              const tierModules = saasTier?.included_modules || [];
              const manualModules = gymData.modules_enabled || [];
              enabledModules = Array.from(new Set([...tierModules, ...manualModules]));
            }
          } catch (e) {
            console.warn('[AuthContext] Failed to restore persisted gym:', e);
            localStorage.removeItem(GYM_SELECTION_KEY);
          }
        }
      }

      setUser(appUser);
      setGym(currentGym);
      setModules(enabledModules.map(m => ({ module_id: m } as Module)));

      // Sentry Tracking
      Sentry.setUser({
        id: appUser.user_id,
        email: appUser.email,
        username: appUser.name,
      });
      Sentry.setTag("role", appUser.role);
      if (currentGym) {
        Sentry.setTag("gym_id", currentGym.gym_id);
        Sentry.setTag("gym_name", currentGym.name);
        Sentry.setTag("gym_slug", currentGym.slug);
      }

    } catch (error) {
      console.error("[AuthContext] Error syncing session:", error);
      reportError(error, { operation: 'auth_sync_session' });
      // Don't force logout, just log the error
      // The user might still have a valid session
    } finally {
      setIsLoading(false);
    }
  }, []); // NO DEPENDENCIES - using refs instead

  // Check session on mount
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      syncSession(session?.user ?? null, true); // Force sync on initial load
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      // Don't force sync on auth state changes - allows us to ignore temporary session changes
      syncSession(session?.user ?? null, false);
    });

    return () => subscription.unsubscribe();
  }, [syncSession]);


  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error("Login Supabase Error:", error);
      setIsLoading(false);
      return false;
    }

    // syncSession will handle the state update via onAuthStateChange
    return true;
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setGym(null);
    setModules([]);
    localStorage.removeItem('gym_saas_user'); // Clear legacy if any
    localStorage.removeItem('gym_saas_token'); // Clear legacy if any
    localStorage.removeItem(GYM_SELECTION_KEY);

    clearSentryUser();
  }, []);

  const hasRole = useCallback((roles: UserRole[]): boolean => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return roles.includes(user.role);
  }, [user]);

  const hasModule = useCallback((moduleId: string): boolean => {
    if (!user) return false;
    // Super admins have access to all modules in global view (no gym selected)
    if (user.role === 'super_admin' && !gym) return true;
    // Otherwise, check if the module is enabled for the current gym
    return modules.some(m => m.module_id === moduleId);
  }, [user, gym, modules]);

  const refreshAuth = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      await syncSession(session.user, true);
    }
  }, [syncSession]);

  const setSelectedGym = useCallback((selectedGym: Gym | null) => {
    // Ensure modules_enabled is always an array and price is explicitly set
    const gymToSet = selectedGym ? {
      ...selectedGym,
      modules_enabled: selectedGym.modules_enabled || [],
      price: selectedGym.price, // Explicitly map price from selectedGym
    } : null;
    setGym(gymToSet);
    if (gymToSet) {
      setModules(gymToSet.modules_enabled.map(m => ({ module_id: m } as Module)));
      localStorage.setItem(GYM_SELECTION_KEY, gymToSet.gym_id);
      
      // Update Sentry Context
      Sentry.setTag("gym_id", gymToSet.gym_id);
      Sentry.setTag("gym_name", gymToSet.name);
      Sentry.setTag("gym_slug", gymToSet.slug);
    } else {
      setModules([]); // No specific modules in global view
      localStorage.removeItem(GYM_SELECTION_KEY);
      
      // Clear Sentry Gym Context
      Sentry.setTag("gym_id", "global");
      Sentry.setTag("gym_name", "Global Session");
      Sentry.setTag("gym_slug", "");
    }
  }, []);

  const sendResetPasswordEmail = useCallback(async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      console.error('Error sending reset password email:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      console.error('Error updating password:', error);
      return { success: false, error: error.message };
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      gym,
      modules,
      isLoading,
      login, // Signatures match
      logout,
      setSelectedGym,
      hasRole,
      hasModule,
      refreshAuth,
      setSessionLock,
      sendResetPasswordEmail,
      updatePassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
